import type { Row } from "@rocicorp/zero";
import type {
  Permission,
  ScopeKind,
} from "../../../../shared/rbac/permissions";
import { uniqueMemberCount } from "../../../lib/memberGroupMap";
import { hiddenScopeLabel, type ResolveScopeName } from "../../../lib/roleChip";
import { playClick } from "../../../lib/sounds";
import { userDisplayName } from "../../../lib/userDisplayName";
import { Avatar } from "../../Avatar";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { ServiceAccountAvatar } from "../../ServiceAccountAvatar";
import { Text } from "../../Text";
import { OverflowBadges } from "../OverflowBadges";
import {
  BuiltinLockBadge,
  Card,
  ClickablePillBadge,
  ColoredIconBadge,
  DetailLabel,
  DetailList,
  DetailRow,
  DetailRowRight,
  FieldLabel,
  ModalFooter,
  NeutralIconBadge,
} from "../shared";
import { DangerSection } from "../DangerSection";
import { SCOPE_ICONS } from "./constants";
import { ModalContainer } from "./ModalContainer";
import { PermissionsList } from "./PermissionsList";
import { navigate, routes } from "../../../lib/urls";

type InspectableRole = {
  id: string;
  name: string;
  scopeKind: ScopeKind;
  permissions: readonly Permission[];
  isBuiltin: boolean;
};

type AssignedGroup = {
  id: string;
  name: string;
  roleGrantsAsScope: ReadonlyArray<{ user?: Row["user"] | null }>;
};

type Props = {
  role: InspectableRole | null;
  onClose: () => void;
  onDelete: (roleId: string) => void;
  canManage?: boolean;
  assignedGroups:
    | readonly {
        group: AssignedGroup;
        scopeIds: string[];
        hiddenScopeCount: number;
      }[]
    | undefined;
  assignedUsers:
    | readonly {
        user: Row["user"];
        scopeIds: string[];
        hiddenScopeCount: number;
      }[]
    | undefined;
  assignedServiceAccounts:
    | readonly {
        serviceAccount: { id: string; name: string };
        scopeIds: string[];
        hiddenScopeCount: number;
      }[]
    | undefined;
  groupMemberCounts: Map<string, number>;
  resolveScopeName: ResolveScopeName;
};

export function InspectRoleModal({
  role,
  onClose,
  onDelete,
  canManage,
  assignedGroups,
  assignedUsers,
  assignedServiceAccounts,
  groupMemberCounts,
  resolveScopeName,
}: Props) {
  if (!role) return null;

  const hasGroups = assignedGroups && assignedGroups.length > 0;
  const hasUsers = assignedUsers && assignedUsers.length > 0;
  const hasServiceAccounts =
    assignedServiceAccounts && assignedServiceAccounts.length > 0;
  const hasAssignments = hasGroups || hasUsers || hasServiceAccounts;
  const scopeKind = role.scopeKind;
  const showScopes = scopeKind !== "tenant";
  const scopeIcon = SCOPE_ICONS[scopeKind];

  const scopeModalType =
    scopeKind === "space"
      ? "space"
      : scopeKind === "connected_app"
        ? "connector"
        : scopeKind === "skill"
          ? "skill"
          : scopeKind === "user_group"
            ? "group"
            : null;

  const renderScopeChips = (
    scopeIds: readonly string[],
    hiddenCount: number
  ) => {
    const items = scopeIds.flatMap((id) => {
      const name = resolveScopeName(id, scopeKind);
      return name ? [{ id, name }] : [];
    });
    if (items.length === 0 && hiddenCount === 0) return null;
    return (
      <DetailRowRight>
        <OverflowBadges
          items={items}
          keyFn={(item) => item.id}
          renderBadge={(item) => (
            <ClickablePillBadge
              icon={scopeIcon}
              onClick={() => {
                if (scopeModalType) {
                  navigate(routes.modal[scopeModalType], { id: item.id });
                }
              }}
            >
              {item.name}
            </ClickablePillBadge>
          )}
          overflowTooltip={(hidden) => hidden.map((h) => h.name).join(", ")}
          extraOverflowCount={hiddenCount}
          extraOverflowLabel={
            hiddenCount > 0
              ? hiddenScopeLabel(hiddenCount, scopeKind)
              : undefined
          }
        />
      </DetailRowRight>
    );
  };

  return (
    <ModalContainer
      open
      onClose={onClose}
      scopeKind={role.scopeKind}
      title={role.name}
      titleExtra={role.isBuiltin ? <BuiltinLockBadge /> : undefined}
      footer={
        <ModalFooter style={{ padding: "12px 16px" }}>
          <Button size="l" onClick={onClose}>
            Done
          </Button>
        </ModalFooter>
      }
    >
      <Card>
        <PermissionsList permissions={role.permissions} />

        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Assigned to</Text>
          </Box>
          {hasAssignments ? (
            <DetailList>
              {assignedGroups?.map(
                ({ group: g, scopeIds, hiddenScopeCount }) => {
                  const count =
                    groupMemberCounts.get(g.id) ??
                    uniqueMemberCount(g.roleGrantsAsScope ?? []);
                  return (
                    <DetailRow
                      key={g.id}
                      data-interactive="true"
                      onMouseDown={() => {
                        playClick();
                        navigate(routes.modal.group, { id: g.id });
                      }}
                    >
                      <ColoredIconBadge id={g.id} iconName="users-round" />
                      <DetailLabel>
                        <Text
                          variant="text-regular-md"
                          color="var(--text-primary)"
                          ellipsis
                        >
                          {g.name}
                        </Text>
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                        >
                          {count} {count === 1 ? "member" : "members"}
                        </Text>
                      </DetailLabel>
                      {showScopes &&
                        scopeKind !== "user_group" &&
                        renderScopeChips(scopeIds, hiddenScopeCount)}
                    </DetailRow>
                  );
                }
              )}
              {assignedUsers?.map(({ user: u, scopeIds, hiddenScopeCount }) => (
                <DetailRow
                  key={u.id}
                  data-interactive="true"
                  onMouseDown={() => {
                    playClick();
                    navigate(routes.modal.member, { id: u.id });
                  }}
                >
                  <Avatar user={u} size={32} />
                  <DetailLabel>
                    <Text
                      variant="text-regular-md"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {userDisplayName(u)}
                    </Text>
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      {u.email}
                    </Text>
                  </DetailLabel>
                  {showScopes && renderScopeChips(scopeIds, hiddenScopeCount)}
                </DetailRow>
              ))}
              {assignedServiceAccounts?.map(
                ({ serviceAccount: sa, scopeIds, hiddenScopeCount }) => (
                  <DetailRow
                    key={sa.id}
                    data-interactive="true"
                    onMouseDown={() => {
                      playClick();
                      navigate(routes.modal.serviceAccount, { id: sa.id });
                    }}
                  >
                    <ServiceAccountAvatar seed={sa.id} />
                    <DetailLabel>
                      <Text
                        variant="text-regular-md"
                        color="var(--text-primary)"
                        ellipsis
                      >
                        {sa.name}
                      </Text>
                      <Text variant="text-small" color="var(--text-secondary)">
                        Service account
                      </Text>
                    </DetailLabel>
                    {showScopes && renderScopeChips(scopeIds, hiddenScopeCount)}
                  </DetailRow>
                )
              )}
            </DetailList>
          ) : (
            <DetailList>
              <DetailRow>
                <NeutralIconBadge iconName="circle-slash" />
                <DetailLabel>
                  <Text variant="text-regular-md" color="var(--text-primary)">
                    Not assigned
                  </Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Assignments show up when groups, users, or service accounts
                    are given this role.
                  </Text>
                </DetailLabel>
              </DetailRow>
            </DetailList>
          )}
        </FieldLabel>
      </Card>
      {canManage && !role.isBuiltin && (
        <DangerSection
          style={{ marginTop: 16 }}
          title="Delete role"
          description="Permanently deletes this role and removes it from all assignments."
          actionLabel="Delete role"
          disabled={Boolean(hasAssignments)}
          disabledReason="Remove all assignments first"
          confirm={{
            confirmLabel: "Delete",
            description: `“${role.name}” will be permanently deleted and removed from all assignments.`,
            onConfirm: () => onDelete(role.id),
          }}
        />
      )}
    </ModalContainer>
  );
}
